import { useCallback, useEffect, useMemo, useState } from 'react';

import type { BizyState } from '../../bizy';
import { wasMeetBizyIntroShownToday } from '../../promo/meet-bizy-intro-day';

const STORAGE_KEY = 'bizy-whats-new-tip';
const LEGACY_STORAGE_KEY = 'bizy-admin-whats-new-tip';

export const PERSONALIZED_WHATS_NEW_TIP_ID = 'personalized-workflows';

export interface WhatsNewTip {
	id: string;
	headlineKey: string;
	promptKey: string;
	state: BizyState;
	/** Internal note; tips with a comment are excluded from the daily rotation. */
	comment?: string;
}

export interface PersonalizedWhatsNewPromptInput {
	id: string;
	prompt: string;
	workflow_rank?: number | null;
}

type PersonalizedBizyWhatsNewTip = {
	kind: 'personalized';
	id: typeof PERSONALIZED_WHATS_NEW_TIP_ID;
	headlineKey: 'whatsNew.tips.personalized.headline';
	promptText: string;
	state: BizyState;
	suggestionId: string;
};

export type BizyWhatsNewTip = ({ kind: 'static' } & WhatsNewTip) | PersonalizedBizyWhatsNewTip;

/** Tips eligible for the admin empty-state daily rotation (no comment set). */
export function isTipInRotation(tip: WhatsNewTip): boolean {
	return !tip.comment?.trim();
}

export const WHATS_NEW_TIPS: WhatsNewTip[] = [
	{
		id: 'program-health',
		headlineKey: 'whatsNew.tips.programHealth.headline',
		promptKey: 'whatsNew.tips.programHealth.prompt',
		state: 'excited',
	},
	{
		id: 'struggling',
		headlineKey: 'whatsNew.tips.struggling.headline',
		promptKey: 'whatsNew.tips.struggling.prompt',
		state: 'excited',
	},
	{
		id: 'culture-champions',
		headlineKey: 'whatsNew.tips.cultureChampions.headline',
		promptKey: 'whatsNew.tips.cultureChampions.prompt',
		state: 'excited',
	},
	{
		id: 'combine-data',
		headlineKey: 'whatsNew.tips.combineData.headline',
		promptKey: 'whatsNew.tips.combineData.prompt',
		state: 'excited',
	},
	{
		id: 'export-data',
		headlineKey: 'whatsNew.tips.exportData.headline',
		promptKey: 'whatsNew.tips.exportData.prompt',
		state: 'excited',
		comment:
			'For admins the link is correct but Bizy answers with activity for the current user only, not company redemptions. Confirm expected behavior for admin vs non-admin before re-enabling.',
	},
	{
		id: 'recognition-gaps',
		headlineKey: 'whatsNew.tips.recognitionGaps.headline',
		promptKey: 'whatsNew.tips.recognitionGaps.prompt',
		state: 'excited',
		comment:
			'Bizy assumes manager-to-direct-report recognition and asks for more info instead of answering. Should answer company-wide and optionally ask a follow-up. Confirm behavior before re-enabling.',
	},
	{
		id: 'strongest-bonds',
		headlineKey: 'whatsNew.tips.strongestBonds.headline',
		promptKey: 'whatsNew.tips.strongestBonds.prompt',
		state: 'excited',
	},
	{
		id: 'super-connectors',
		headlineKey: 'whatsNew.tips.superConnectors.headline',
		promptKey: 'whatsNew.tips.superConnectors.prompt',
		state: 'excited',
	},
	{
		id: 'new-hires',
		headlineKey: 'whatsNew.tips.newHires.headline',
		promptKey: 'whatsNew.tips.newHires.prompt',
		state: 'excited',
		comment: 'Excluded from rotation, prompt needs to be revised.',
	},
];

interface StoredTipState {
	lastShownDate: string;
	lastTipIndex: number;
	dismissed: boolean;
	/** Tip IDs the user has already been shown; tips listed here are never re-assigned. */
	shownTipIds?: string[];
	/** Fingerprint of the personalized suggestion batch currently in rotation (workflow ranks). */
	personalizedGenerationKey?: string;
	/** Workflow ranks already shown in What's New for this batch. */
	shownPersonalizedWorkflowRanks?: number[];
	/** @deprecated Legacy suggestion IDs; migrated to {@link shownPersonalizedWorkflowRanks}. */
	shownPersonalizedSuggestionIds?: string[];
	/** Workflow rank assigned for the current UTC day, if any. */
	lastPersonalizedWorkflowRank?: number;
	/** @deprecated Legacy suggestion ID; migrated to {@link lastPersonalizedWorkflowRank}. */
	lastPersonalizedSuggestionId?: string;
	/** @deprecated Legacy batch dismiss; treated as all suggestions in that batch shown. */
	personalizedDismissedGenerationKey?: string;
}

export type BizyWhatsNewTipStoredState = StoredTipState;

export const BIZY_WHATS_NEW_TIP_STORAGE_KEY = STORAGE_KEY;
export const BIZY_WHATS_NEW_TIP_LEGACY_STORAGE_KEY = LEGACY_STORAGE_KEY;

export function getDateKey(): string {
	return new Date().toISOString().split('T')[0];
}

function getStoredState(): StoredTipState | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) return JSON.parse(stored) as StoredTipState;

		const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
		if (!legacy) return null;

		localStorage.setItem(STORAGE_KEY, legacy);
		localStorage.removeItem(LEGACY_STORAGE_KEY);
		return JSON.parse(legacy) as StoredTipState;
	} catch {
		return null;
	}
}

function setStoredState(state: StoredTipState): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch {
		// localStorage unavailable
	}
}

export function readBizyWhatsNewTipStoredState(): BizyWhatsNewTipStoredState | null {
	return getStoredState();
}

export function writeBizyWhatsNewTipStoredState(state: BizyWhatsNewTipStoredState): void {
	setStoredState(state);
	syncBizyWhatsNewTipStorageListeners();
}

export function getRotatingWhatsNewTipIds(): string[] {
	return WHATS_NEW_TIPS.filter(isTipInRotation).map((tip) => tip.id);
}

/** Notifies open Bizy panels to re-read localStorage (same-tab dev helpers). */
export function syncBizyWhatsNewTipStorageListeners(): void {
	window.dispatchEvent(new Event('focus'));
}

function mergeStoredState(patch: Partial<StoredTipState>): void {
	const stored = getStoredState();
	setStoredState({
		lastShownDate: stored?.lastShownDate ?? getDateKey(),
		lastTipIndex: stored?.lastTipIndex ?? -1,
		dismissed: stored?.dismissed ?? false,
		shownTipIds: getShownTipIds(stored),
		personalizedGenerationKey: stored?.personalizedGenerationKey,
		shownPersonalizedWorkflowRanks: stored?.shownPersonalizedWorkflowRanks,
		shownPersonalizedSuggestionIds: stored?.shownPersonalizedSuggestionIds,
		lastPersonalizedWorkflowRank: stored?.lastPersonalizedWorkflowRank,
		lastPersonalizedSuggestionId: stored?.lastPersonalizedSuggestionId,
		personalizedDismissedGenerationKey: stored?.personalizedDismissedGenerationKey,
		...patch,
	});
}

/**
 * Returns the IDs of tips the user has already been shown.
 *
 * For state stored before `shownTipIds` existed, every rotating tip at indices
 * `[0..lastTipIndex]` is treated as already shown — that's the most we can infer
 * from the legacy single-index cursor.
 */
export function getShownTipIds(stored: StoredTipState | null): string[] {
	if (!stored) return [];
	if (stored.shownTipIds) return stored.shownTipIds;
	const through = stored.lastTipIndex;
	if (through === undefined || through < 0) return [];
	return WHATS_NEW_TIPS.slice(0, through + 1)
		.filter(isTipInRotation)
		.map((tip) => tip.id);
}

function isRankBasedGenerationKey(key: string): boolean {
	const parts = key.split(':');
	return parts.length > 0 && parts.every((part) => /^\d+$/.test(part));
}

function legacyGenerationMatchesCurrentBatch(legacyKey: string, currentKey: string): boolean {
	if (isRankBasedGenerationKey(legacyKey)) return legacyKey === currentKey;
	return legacyKey.split(':').length === currentKey.split(':').length;
}

function readShownPersonalizedWorkflowRanks(stored: StoredTipState, prompts: readonly PersonalizedWhatsNewPromptInput[]): number[] {
	if (stored.shownPersonalizedWorkflowRanks?.length) {
		return stored.shownPersonalizedWorkflowRanks;
	}

	if (!stored.shownPersonalizedSuggestionIds?.length) {
		return [];
	}

	return stored.shownPersonalizedSuggestionIds
		.map((id) => prompts.find((prompt) => prompt.id === id)?.workflow_rank)
		.filter((rank): rank is number => rank != null);
}

/**
 * Returns workflow ranks already shown for the current batch.
 * Resets when the generation key changes. Migrates legacy batch-dismiss state
 * and suggestion-id cursors from older builds.
 */
export function getShownPersonalizedWorkflowRanks(
	stored: StoredTipState | null,
	generationKey: string | null,
	prompts: readonly PersonalizedWhatsNewPromptInput[] = []
): number[] {
	if (!stored || !generationKey) return [];

	if (
		stored.personalizedGenerationKey === generationKey ||
		(stored.personalizedGenerationKey && legacyGenerationMatchesCurrentBatch(stored.personalizedGenerationKey, generationKey))
	) {
		return readShownPersonalizedWorkflowRanks(stored, prompts);
	}

	if (stored.personalizedDismissedGenerationKey === generationKey) {
		return generationKey
			.split(':')
			.map(Number)
			.filter((rank) => !Number.isNaN(rank));
	}

	return [];
}

/** @deprecated Prefer {@link getShownPersonalizedWorkflowRanks}. */
export function getShownPersonalizedSuggestionIds(stored: StoredTipState | null, generationKey: string | null): string[] {
	return getShownPersonalizedWorkflowRanks(stored, generationKey).map(String);
}

/**
 * Returns the index of the next rotating tip the user hasn't been shown yet,
 * scanning `WHATS_NEW_TIPS` in order. Returns `null` once every rotating tip
 * has been shown, so callers can stop displaying tips entirely.
 */
export function findNextUnshownTipIndex(shownTipIds: readonly string[]): number | null {
	for (let i = 0; i < WHATS_NEW_TIPS.length; i++) {
		const tip = WHATS_NEW_TIPS[i];
		if (tip && isTipInRotation(tip) && !shownTipIds.includes(tip.id)) {
			return i;
		}
	}
	return null;
}

export function buildPersonalizedGenerationKey(prompts: readonly PersonalizedWhatsNewPromptInput[]): string | null {
	const ranks = prompts
		.map((prompt) => prompt.workflow_rank)
		.filter((rank): rank is number => rank != null)
		.sort((a, b) => a - b);
	if (ranks.length === 0) return null;
	return ranks.join(':');
}

export function pickNextPersonalizedPrompt(
	prompts: readonly PersonalizedWhatsNewPromptInput[],
	shownWorkflowRanks: readonly number[] = []
): PersonalizedWhatsNewPromptInput | null {
	const eligible = prompts.filter((prompt) => prompt.prompt?.trim() && prompt.workflow_rank != null && !shownWorkflowRanks.includes(prompt.workflow_rank));
	if (eligible.length === 0) return null;

	return [...eligible].sort((a, b) => (a.workflow_rank ?? Number.MAX_SAFE_INTEGER) - (b.workflow_rank ?? Number.MAX_SAFE_INTEGER))[0];
}

export function isPersonalizedBatchExhausted(prompts: readonly PersonalizedWhatsNewPromptInput[], shownWorkflowRanks: readonly number[]): boolean {
	const ranks = prompts.map((prompt) => prompt.workflow_rank).filter((rank): rank is number => rank != null);
	if (ranks.length === 0) return false;
	return ranks.every((rank) => shownWorkflowRanks.includes(rank));
}

/** Whether the current personalized batch has no remaining What's New or chip prompts. */
export function isPersonalizedWhatsNewBatchExhausted(prompts: readonly PersonalizedWhatsNewPromptInput[]): boolean {
	const generationKey = buildPersonalizedGenerationKey(prompts);
	if (!generationKey) return false;

	const shownWorkflowRanks = getShownPersonalizedWorkflowRanks(getStoredState(), generationKey, prompts);
	return isPersonalizedBatchExhausted(prompts, shownWorkflowRanks);
}

/** @deprecated Prefer {@link pickNextPersonalizedPrompt} with an empty shown list. */
export function pickTopPersonalizedPrompt(prompts: readonly PersonalizedWhatsNewPromptInput[]): PersonalizedWhatsNewPromptInput | null {
	return pickNextPersonalizedPrompt(prompts, []);
}

function stableHash(input: string): number {
	let hash = 0;
	for (let i = 0; i < input.length; i++) {
		hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
	}
	return hash;
}

/**
 * Picks one personalized winner for empty-state prompt chips. Stable for a
 * given generation fingerprint and seed (typically the UTC date) so the chip
 * does not shuffle on re-render.
 */
export function pickRandomPersonalizedChipPrompt(
	prompts: readonly PersonalizedWhatsNewPromptInput[],
	generationKey: string | null,
	seed: string
): PersonalizedWhatsNewPromptInput | null {
	const eligible = [...prompts].filter((prompt) => prompt.prompt?.trim()).sort((a, b) => a.id.localeCompare(b.id));
	if (eligible.length === 0) return null;
	if (eligible.length === 1) return eligible[0];

	const index = stableHash(`${generationKey ?? ''}:${seed}`) % eligible.length;
	return eligible[index] ?? null;
}

function buildPersonalizedTip(prompt: PersonalizedWhatsNewPromptInput): BizyWhatsNewTip {
	return {
		kind: 'personalized',
		id: PERSONALIZED_WHATS_NEW_TIP_ID,
		headlineKey: 'whatsNew.tips.personalized.headline',
		promptText: prompt.prompt.trim(),
		state: 'excited',
		suggestionId: prompt.id,
	};
}

interface UseBizyWhatsNewTipOptions {
	/** When true, static admin tips rotate after personalized suggestions are exhausted. */
	canShowStaticTips: boolean;
	isEmptyState: boolean;
	isPanelOpen: boolean;
	personalizedPrompts?: PersonalizedWhatsNewPromptInput[];
	/** When false, defer static tips until we know whether personalized winners exist. */
	personalizedPromptsResolved?: boolean;
}

interface UseBizyWhatsNewTipResult {
	tip: BizyWhatsNewTip | null;
	dismiss: () => void;
	shouldShowTip: boolean;
	/** Current UTC date key (`YYYY-MM-DD`); updates at midnight for daily rotation seeds. */
	dateKey: string;
	/**
	 * True when there is a tip assigned for today that the user has not yet
	 * dismissed, regardless of whether it's currently visible (panel closed,
	 * chat non-empty, etc.). Useful for engagement-based dismissal triggers
	 * that need to fire when the tip would otherwise re-appear later.
	 */
	hasActiveTip: boolean;
}

/**
 * Picks which "What's new" tip to show in the empty Bizy panel.
 *
 * Personalized workflow prompts run for any user with winners: one suggestion
 * per UTC day (by workflow rank) until the batch is exhausted. Admins then
 * continue with static tips. Static tips rotate one per day and never advance
 * while personalized suggestions remain. When the set of workflow ranks in the
 * batch changes, only the personalized track resets.
 */
function arraysShallowEqual(a: readonly string[], b: readonly string[]): boolean {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

export function useBizyWhatsNewTip({
	canShowStaticTips,
	isEmptyState,
	isPanelOpen,
	personalizedPrompts = [],
	personalizedPromptsResolved = true,
}: UseBizyWhatsNewTipOptions): UseBizyWhatsNewTipResult {
	const [shownTipIds, setShownTipIds] = useState<string[]>(() => getShownTipIds(getStoredState()));
	const [dateKey, setDateKey] = useState(() => getDateKey());
	const [isDismissedToday, setIsDismissedToday] = useState(() => {
		const stored = getStoredState();
		return Boolean(stored && stored.lastShownDate === getDateKey() && stored.dismissed);
	});

	const personalizedGenerationKey = useMemo(() => buildPersonalizedGenerationKey(personalizedPrompts), [personalizedPrompts]);

	useEffect(() => {
		const sync = () => {
			const stored = getStoredState();
			const storedIds = getShownTipIds(stored);
			setShownTipIds((prev) => (arraysShallowEqual(prev, storedIds) ? prev : storedIds));
			const current = getDateKey();
			setDateKey((prev) => (prev === current ? prev : current));
			setIsDismissedToday(Boolean(stored && stored.lastShownDate === current && stored.dismissed));
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') sync();
		};
		const onStorage = (event: StorageEvent) => {
			if (event.key === STORAGE_KEY || event.key === LEGACY_STORAGE_KEY) sync();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('focus', sync);
		window.addEventListener('storage', onStorage);

		let midnightTimeoutId = 0;
		const scheduleMidnight = () => {
			const now = new Date();
			const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 5);
			midnightTimeoutId = window.setTimeout(() => {
				sync();
				scheduleMidnight();
			}, next - now.getTime());
		};
		scheduleMidnight();

		return () => {
			document.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('focus', sync);
			window.removeEventListener('storage', onStorage);
			window.clearTimeout(midnightTimeoutId);
		};
	}, []);

	const { personalizedTip, personalizedNeedsPersist, personalizedWorkflowRank, currentDate } = useMemo(() => {
		const today = getDateKey();
		if (wasMeetBizyIntroShownToday() || !personalizedGenerationKey) {
			return {
				personalizedTip: null,
				personalizedNeedsPersist: false,
				personalizedWorkflowRank: null,
				currentDate: today,
			};
		}

		const stored = getStoredState();
		const shownPersonalizedRanks = getShownPersonalizedWorkflowRanks(stored, personalizedGenerationKey, personalizedPrompts);
		const personalizedBatchExhausted = isPersonalizedBatchExhausted(personalizedPrompts, shownPersonalizedRanks);
		const nextPrompt = pickNextPersonalizedPrompt(personalizedPrompts, shownPersonalizedRanks);
		const resumeRank =
			stored?.lastPersonalizedWorkflowRank ??
			(stored?.lastPersonalizedSuggestionId
				? personalizedPrompts.find((prompt) => prompt.id === stored.lastPersonalizedSuggestionId)?.workflow_rank
				: undefined);
		const resumePrompt = resumeRank != null ? personalizedPrompts.find((prompt) => prompt.workflow_rank === resumeRank) : undefined;

		if (stored && stored.lastShownDate === today) {
			if (stored.dismissed) {
				return {
					personalizedTip: null,
					personalizedNeedsPersist: false,
					personalizedWorkflowRank: null,
					currentDate: today,
				};
			}

			if (
				resumePrompt?.prompt?.trim() &&
				!personalizedBatchExhausted &&
				stored.personalizedGenerationKey &&
				(stored.personalizedGenerationKey === personalizedGenerationKey ||
					legacyGenerationMatchesCurrentBatch(stored.personalizedGenerationKey, personalizedGenerationKey))
			) {
				return {
					personalizedTip: buildPersonalizedTip(resumePrompt),
					personalizedNeedsPersist: false,
					personalizedWorkflowRank: resumePrompt.workflow_rank ?? null,
					currentDate: today,
				};
			}

			// Static may have been assigned earlier when the personalized batch was still
			// empty (e.g. React Query cache). Prefer personalized while any remain.
			if (canShowStaticTips && !nextPrompt && stored.lastTipIndex >= 0 && WHATS_NEW_TIPS[stored.lastTipIndex]) {
				return {
					personalizedTip: null,
					personalizedNeedsPersist: false,
					personalizedWorkflowRank: null,
					currentDate: today,
				};
			}
		}

		if (!nextPrompt) {
			return {
				personalizedTip: null,
				personalizedNeedsPersist: false,
				personalizedWorkflowRank: null,
				currentDate: today,
			};
		}

		return {
			personalizedTip: buildPersonalizedTip(nextPrompt),
			personalizedNeedsPersist: true,
			personalizedWorkflowRank: nextPrompt.workflow_rank ?? null,
			currentDate: today,
		};
	}, [canShowStaticTips, personalizedPrompts, personalizedGenerationKey, dateKey, isDismissedToday]);

	const { staticTip, tipIndex, staticNeedsPersist } = useMemo(() => {
		const today = getDateKey();
		if (!canShowStaticTips || personalizedTip) {
			return { staticTip: null, tipIndex: -1, staticNeedsPersist: false };
		}

		if (wasMeetBizyIntroShownToday()) {
			return { staticTip: null, tipIndex: -1, staticNeedsPersist: false };
		}

		if (!personalizedPromptsResolved) {
			return { staticTip: null, tipIndex: -1, staticNeedsPersist: false };
		}

		const stored = getStoredState();

		if (stored && stored.lastShownDate === today) {
			if (stored.dismissed) {
				return { staticTip: null, tipIndex: stored.lastTipIndex, staticNeedsPersist: false };
			}
			const storedTip = WHATS_NEW_TIPS[stored.lastTipIndex];
			if (storedTip && isTipInRotation(storedTip)) {
				return { staticTip: storedTip, tipIndex: stored.lastTipIndex, staticNeedsPersist: false };
			}
		}

		const nextIndex = findNextUnshownTipIndex(shownTipIds);
		if (nextIndex === null) {
			return { staticTip: null, tipIndex: -1, staticNeedsPersist: false };
		}
		const nextTip = WHATS_NEW_TIPS[nextIndex];
		return { staticTip: nextTip, tipIndex: nextIndex, staticNeedsPersist: true };
	}, [canShowStaticTips, personalizedTip, personalizedPromptsResolved, shownTipIds, dateKey, isDismissedToday]);

	useEffect(() => {
		if (!personalizedNeedsPersist || personalizedWorkflowRank == null || !personalizedGenerationKey) return;

		const stored = getStoredState();
		mergeStoredState({
			lastShownDate: currentDate,
			lastTipIndex: stored?.lastTipIndex ?? -1,
			dismissed: false,
			lastPersonalizedWorkflowRank: personalizedWorkflowRank,
			personalizedGenerationKey,
		});
	}, [personalizedNeedsPersist, personalizedWorkflowRank, personalizedGenerationKey, currentDate]);

	useEffect(() => {
		if (!staticNeedsPersist) return;
		mergeStoredState({
			lastShownDate: currentDate,
			lastTipIndex: tipIndex,
			dismissed: false,
			lastPersonalizedWorkflowRank: undefined,
			lastPersonalizedSuggestionId: undefined,
		});
	}, [staticNeedsPersist, tipIndex, currentDate]);

	const tip = useMemo((): BizyWhatsNewTip | null => {
		if (personalizedTip) return personalizedTip;
		if (!staticTip) return null;
		return { kind: 'static', ...staticTip };
	}, [personalizedTip, staticTip]);

	const staticTipActive = staticTip !== null && !isDismissedToday;
	const personalizedTipActive = personalizedTip !== null && !isDismissedToday;
	const shouldShowTip = isEmptyState && isPanelOpen && (personalizedTipActive || staticTipActive);

	useEffect(() => {
		if (!shouldShowTip || !tip || tip.kind !== 'personalized' || !personalizedGenerationKey) return;

		const stored = getStoredState();
		const workflowRank = personalizedPrompts.find((prompt) => prompt.id === tip.suggestionId)?.workflow_rank;
		if (workflowRank == null) return;

		const shownPersonalizedRanks = getShownPersonalizedWorkflowRanks(stored, personalizedGenerationKey, personalizedPrompts);
		if (shownPersonalizedRanks.includes(workflowRank)) return;

		const updatedPersonalizedRanks = [...shownPersonalizedRanks, workflowRank];
		mergeStoredState({
			lastShownDate: getDateKey(),
			lastTipIndex: stored?.lastTipIndex ?? -1,
			dismissed: stored?.dismissed ?? false,
			lastPersonalizedWorkflowRank: workflowRank,
			personalizedGenerationKey,
			shownPersonalizedWorkflowRanks: updatedPersonalizedRanks,
			shownPersonalizedSuggestionIds: undefined,
			lastPersonalizedSuggestionId: undefined,
			personalizedDismissedGenerationKey: undefined,
		});
	}, [shouldShowTip, tip, personalizedGenerationKey, personalizedPrompts]);

	useEffect(() => {
		if (!shouldShowTip || !tip || tip.kind !== 'static') return;

		const stored = getStoredState();
		const storedIds = getShownTipIds(stored);
		if (storedIds.includes(tip.id)) {
			if (!arraysShallowEqual(shownTipIds, storedIds)) {
				setShownTipIds(storedIds);
			}
			return;
		}
		const updated = [...storedIds, tip.id];
		setShownTipIds(updated);
		mergeStoredState({
			lastShownDate: getDateKey(),
			lastTipIndex: tipIndex,
			dismissed: stored?.dismissed ?? false,
			shownTipIds: updated,
			lastPersonalizedWorkflowRank: undefined,
			lastPersonalizedSuggestionId: undefined,
		});
	}, [shouldShowTip, tip, tipIndex, shownTipIds]);

	const dismiss = useCallback(() => {
		const stored = getStoredState();
		const personalizedWorkflowRank =
			tip?.kind === 'personalized'
				? (personalizedPrompts.find((prompt) => prompt.id === tip.suggestionId)?.workflow_rank ?? stored?.lastPersonalizedWorkflowRank)
				: stored?.lastPersonalizedWorkflowRank;
		mergeStoredState({
			lastShownDate: getDateKey(),
			lastTipIndex: stored?.lastTipIndex ?? tipIndex,
			dismissed: true,
			lastPersonalizedWorkflowRank: personalizedWorkflowRank,
		});
		setIsDismissedToday(true);
	}, [tip, tipIndex, personalizedPrompts]);

	const hasActiveTip = personalizedTipActive || staticTipActive;

	return {
		tip,
		dismiss,
		shouldShowTip,
		hasActiveTip,
		dateKey,
	};
}
